import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Highlight } from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import { FontFamily } from "@tiptap/extension-font-family";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Extension } from "@tiptap/core";
import ImageResize from "tiptap-extension-resize-image";
import Blockquote from "@tiptap/extension-blockquote";
import Strike from "@tiptap/extension-strike";
import CodeBlock from "@tiptap/extension-code-block";
import Heading from "@tiptap/extension-heading";
import Paragraph from "@tiptap/extension-paragraph";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table";
import { TableHeader } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table";
import juice from "juice";

import EditorToolbar from "./EditorToolBar";
import ResourceManagerModal from "views/Components/ResourceLibrary/ResourceManagerModal";
// Extract styles from HTML and convert class-based styles to inline
const prepareContentForEditor = (html) => {
  // Use juice to inline the styles from <style> tags
  const inlined = juice(html, {
    inlinePseudoElements: false,
    preserveImportant: true,
    removeStyleTags: false,
  });

  // Parse and extract just the body content
  const parser = new DOMParser();
  const doc = parser.parseFromString(inlined, "text/html");

  // Convert inline styles to TipTap-compatible format
  const processElement = (element) => {
    // Process children first
    Array.from(element.children).forEach(processElement);
    const style = element.style;

    // Wrap text with styled spans for color preservation
    if (style.color) {
      const color = style.color;
      // Keep the color as inline style
      // element.setAttribute("style", `color: ${color}`);
      element.style.color = color;
    }

    if (style.backgroundColor) {
      element.style.backgroundColor = style.backgroundColor;
    }

    // PRESERVE FONT SIZE
    if (style.fontSize) {
      const fontSize = style.fontSize;

      const span = doc.createElement("span");
      span.style.fontSize = fontSize;

      while (element.firstChild) {
        span.appendChild(element.firstChild);
      }

      element.appendChild(span);
      element.style.fontSize = "";
    }
  };

  processElement(doc.body);

  return doc.body.innerHTML;
};

// custom font-size mark using Extension.create (keeps your original logic)
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => {
              const size = el.style.fontSize;
              if (!size) return null;
              return parseInt(size.replace("px", ""));
            },

            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}px` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ commands }) => {
          return commands.setMark("textStyle", { fontSize });
        },
      unsetFontSize:
        () =>
        ({ commands }) => {
          return commands.setMark("textStyle", { fontSize: null });
        },
    };
  },
});

const CustomHeading = Heading.extend({
  addOptions() {
    return {
      levels: [1, 2, 3],
      HTMLAttributes: { style: "text-align: inherit;" },
    };
  },
  parseHTML() {
    return [
      { tag: "h1", attrs: { level: 1 } },
      { tag: "h2", attrs: { level: 2 } },
      { tag: "h3", attrs: { level: 3 } },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const level = node.attrs.level || 1;
    return [`h${level}`, HTMLAttributes, 0];
  },
});

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      // fontSize: { default: null },
      color: { default: null },
      fontStyle: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: "p",
        getAttrs: (el) => {
          // Read node-level styles
          const nodeAttrs = {};
          // if (el.style.fontSize) {
          //   nodeAttrs.fontSize = parseInt(el.style.fontSize.replace("px", ""));
          // }
          if (el.style.color) nodeAttrs.color = el.style.color;
          if (el.style.fontStyle) nodeAttrs.fontStyle = el.style.fontStyle;
          if (el.style.textAlign) nodeAttrs.textAlign = el.style.textAlign;

          return nodeAttrs;
        },
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    let style = "";
    // if (node.attrs.fontSize)
    //   style += `font-size: ${node.attrs.fontSize} !important;`;
    if (node.attrs.color) style += `color: ${node.attrs.color} !important;`;
    if (node.attrs.fontStyle)
      style += `font-style: ${node.attrs.fontStyle} !important;`;
    if (node.attrs.textAlign)
      style += `text-align: ${node.attrs.textAlign} !important;`;

    return ["p", { ...HTMLAttributes, style }, 0];
  },
});
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});
export default function TiptapEditor({ onSave, initialContent = "" }) {
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [originalAIHtml, setOriginalAIHtml] = useState(initialContent);
  const preparedContent = useMemo(
    () => prepareContentForEditor(initialContent),
    [initialContent],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // disable built-in heading
        history: { depth: 100 },
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          style:
            "width:100% !important; border-collapse:collapse; font-family:Arial, sans-serif; border-bottom:2px solid #000;",
        },
      }),
      TableRow,
      TableHeader,
      CustomTableCell,
      CustomParagraph,
      Underline,
      Strike,
      CodeBlock,
      Blockquote,
      CustomHeading,
      TextAlign.configure({
        types: ["heading", "paragraph", "image", "tableCell", "tableHeader"],
      }),
      Highlight.configure({ multicolor: true }),
      ImageResize.configure({
        allowBase64: true,
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      FontFamily.configure({ types: ["textStyle"] }),
      TextStyle.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      FontSize,
    ],
    content: preparedContent || "<p>Start typing...</p>",
    editorProps: {
      attributes: {
        class: "tiptap focus:outline-none p-8 min-h-[500px]",
      },
    },
    onUpdate: ({ editor }) => {
      const tiptapHtml = editor.getHTML();
      onSave?.(tiptapHtml);
    },
  });

  useEffect(() => {
    if (selectedResource && editor) {
      editor.chain().focus().setImage({ src: selectedResource }).run();
      onSave?.(editor.getHTML());
      setSelectedResource(null);
    }
  }, [selectedResource, editor]);

  const fileInputRef = useRef(null);

  const insertImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageSelect = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file || !editor) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result;
        // Insert image
        editor.chain().focus().setImage({ src }).run();

        // Immediately update content string with new HTML
        onSave?.(editor.getHTML());
      };
      reader.readAsDataURL(file);

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [editor, onSave],
  );

  if (!editor) return <div className="p-4">Loading editor...</div>;

  return (
    <>
      <div className="w-full h-[80vh] flex flex-col border border-border rounded-lg overflow-hidden">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />
        <div className="flex-shrink-0 border-b border-border z-10">
          <EditorToolbar
            editor={editor}
            onInsertImage={insertImage}
            onShowResources={() => setShowResourceModal(true)}
          />
        </div>

        <div className="flex-1 overflow-auto">
          <EditorContent
            editor={editor}
            className="p-4 min-h-[500px] tiptap-editor focus:outline-none"
          />
        </div>
      </div>
      <ResourceManagerModal
        show={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        onSelectResource={(url) => setSelectedResource(url)}
      />
    </>
  );
}
